<script setup>
// Imports
import { ref, computed, onMounted, onBeforeUnmount } from "vue"
import { useI18n } from "vue-i18n" // Add this missing import
import { useDisplay } from "vuetify"
import { generateUniqueId, formatNumber } from "@/utils/strings"
import { useEventStore } from "@/stores/EventsStore"
import { useProductsStore } from "@/stores/ProductsStore"
import { useCalendarStore } from "@/views/apps/calendar/useCalendarStore"
import { useModalStore } from "@/stores/ModalStore"
import { useServiceStore } from "@/stores/ServicesStore"
import { filterByName } from "@/utils/filters"
import { adjustMobileScroll } from "@/utils/mobile"
import { extractDate } from "@/utils/datetime"
import DatePicker from "@/components/DatePicker.vue"
import EditProductDialog from "@/components/dialogs/EditProductDialog.vue"
import MobileAutocomplete from "@/components/MobileAutocomplete.vue"
import EmployeeAvatar from "@/components/EmployeeAvatar.vue"
import EditStorageProductDialog from "@/components/dialogs/storage/EditStorageProductDialog.vue"
import AddSupplierDialog from "@/components/dialogs/storage/AddSupplierDialog.vue"

const props = defineProps({
  inCollapse: {
    type: Boolean,
    required: false,
  },
})

// Store initialization
const { t } = useI18n()
const eventStore = useEventStore()
const productsStore = useProductsStore()
const calendarStore = useCalendarStore()
const modalStore = useModalStore()
const serviceStore = useServiceStore()
const { mobile, xs } = useDisplay()

// Form state
const selectedProduct = ref(null) // Selected product from the autocomplete
const productDestinationError = ref('') // Error message for product destination selection
//const selectedProductType = ref(1) // Default to sale (1)
const selectedSeller = ref(null) // Selected seller for the product
const tempSelectedEmployee = ref() // Temporary storage for selected employee when editing product seller
const productAutocompleteRef = ref(null) // Reference to the product autocomplete component
const editProductDialog = ref() // Reference to the edit product dialog component
const currentEditingProductDynamicId = ref(null) // store dynamicId for the product being edited

// 👉 Filter out product list based on selected destination
const filteredProducts = computed(() => {
  // If no destination selected or no products, return empty array
  if (!eventStore.selectedProductType || !productsStore.fetchedProducts.length)
    return []

  const filtered = productsStore.fetchedProducts.filter(product => {
    if (eventStore.selectedProductType === 1) {
      // Sale: Show types 1 and 3
      return Number(product.type) === 1 || Number(product.type) === 3
    } else if (eventStore.selectedProductType === 2) {
      // Consumption: Show types 2 and 3
      return Number(product.type) === 2 || Number(product.type) === 3
    }

    return true // Fallback: show all products
  })

  return [...new Map(filtered.map(p => [p.id, p])).values()]

})

// 👉 Add selected product to the event
const addProduct = async (tmpSelectedProduct) => {
  if (!eventStore.selectedProductType) {
    productDestinationError.value = t('validmsg.select')

    return
  }

  productDestinationError.value = ''
  tempSelectedEmployee.value = selectedSeller.value

  if (tmpSelectedProduct) {
    const price = eventStore.selectedProductType === 2 ? 0 : tmpSelectedProduct.price
    let newProduct = {}

    newProduct = { ...tmpSelectedProduct,
      price: price,
      quantity: 1,
      seller: selectedSeller.value,
      discountType: 'solid',
      discountValue: null,
      type: Number(eventStore.selectedProductType),
      dynamicId: generateUniqueId(),
    }

    if(tmpSelectedProduct.isVoucher) {
      newProduct = {
        ...newProduct,
        voucherExpirationDate: getVoucherExpirationDate(eventStore.selectedDate, tmpSelectedProduct.voucherDetails?.timeout),
      }
      newProduct.voucherDetails.timeout = extractDate(getVoucherExpirationDate(eventStore.selectedDate, tmpSelectedProduct.voucherDetails?.timeout), '-')
    }

    // Apply store pricing calculations for consistency
    newProduct = productsStore.updateProductPricing(newProduct)
    eventStore.selectedProducts.push(newProduct)
  }

  productAutocompleteRef.value?.blur()
  selectedProduct.value = null
  productsStore.fetchedProducts = []

}

// 👉 Product management
const openEditProductDialog = (product, idx) => {
  editProductDialog.value.initializeDialog(product, idx)
  modalStore.openModal('editProduct')
}

// Storage product edit link - hidden when visit can't be edited (e.g. finalised)
const showProductStoreLink = computed(() => calendarStore.viewSettings.showProductStoreLink && eventStore.allowSave)

/**
 * Opens storage product edit dialog and applies saved changes to every line with this product.
 * Name, unit and capacity are always refreshed; sale price only after user confirms.
 */
const openStorageProductDialog = async product => {
  const saved = await modalStore.openModal('editStorageProduct', '', false, { productId: product.id })
  if (!saved) return

  const sameProducts = eventStore.selectedProducts.filter(p => Number(p.id) === Number(saved.id) && !p.isVoucher)

  sameProducts.forEach(p => {
    p.title = saved.name
    p.unit = saved.unit
    p.capacity = `${saved.package} ${saved.unit}`
  })

  const saleLines = sameProducts.filter(p => Number(p.type) === 1)
  const newPrice = Number(saved.price) || 0
  const previousPrice = Number(saved.previousPrice) || 0
  if (!saleLines.length || newPrice === previousPrice) return

  const confirmed = await modalStore.openConfirmationModal(
    t('storagePriceChangedUpdateSale', {
      from: `${formatNumber(previousPrice)} ${calendarStore.currency}`,
      to: `${formatNumber(newPrice)} ${calendarStore.currency}`,
    }),
    t('storagePriceChanged'),
    {
      confirmText: t('yes'),
      cancelText: t('no'),
    },
  )
  if (!confirmed) return

  saleLines.forEach(p => {
    p.price = newPrice
    productsStore.updateProductPricing(p)
  })
}

const removeProduct = (product) => {
  const dynamicId = product.dynamicId

  //Remove product by dynamicId from selectedProducts
  eventStore.selectedProducts = eventStore.selectedProducts.filter(
    p => p.dynamicId !== dynamicId,
  )
}

const calculateValue = (product) => {
  return productsStore.calculateProductValue(product.price, product.quantity)
}

// 👉 Seller management
const openSellerDialog = (product) => {
  currentEditingProductDynamicId.value = product?.dynamicId ?? null
  tempSelectedEmployee.value = product?.seller
  modalStore.openModal('selectSeller')
}

const saveSelectedSeller = () => {
  const dynamicId = currentEditingProductDynamicId.value

  if (!dynamicId) {
    console.warn('No dynamicId provided to saveSelectedSeller')
    modalStore.closeModal('selectSeller')

    return
  }

  const index = eventStore.selectedProducts.findIndex(p => p.dynamicId === dynamicId)
  if (index === -1) {
    console.warn('Product not found for dynamicId:', dynamicId)
    modalStore.closeModal('selectSeller')

    return
  }

  // assign selected seller and close modal
  eventStore.selectedProducts[index].seller = tempSelectedEmployee.value

  currentEditingProductDynamicId.value = null //reset
  modalStore.closeModal('selectSeller')
}

const uniqueServicesForFiltering = computed(() => {
  const uniqueServicesMap = new Map()
  serviceStore.servicesArray
    .filter(service => !service.deleted && service.price >= 0 && service.duration >= 0)
    .forEach(service => {
      if (!uniqueServicesMap.has(service.id)) {
        uniqueServicesMap.set(service.id, {
          uniqueId: generateUniqueId(),
          id: service.id,
          name: service.name,
          price: service.price,
          priceMax: service.priceMax,
          duration: service.duration,
        })
      }
    })

  return Array.from(uniqueServicesMap.values())
})

// Product type filters
const saleProducts = computed(() =>
  [...eventStore.selectedProducts]
    .filter(product => Number(product.type) === 1),
)
const consumptionProducts = computed(() =>
  [...eventStore.selectedProducts]
    .filter(product => Number(product.type) === 2),
)

// Formats voucher expiration information based on the provided input.
// The input can be either a number/string representing a number of days (e.g. 365, "365")
// or a date string (e.g. "2025-12-30").
// For day-based values, it returns a translated label like "Valid for: 365 days".
// For date values, it formats the date using extractDate() and returns "Valid until: DD.MM.YYYY".
// Returns an empty string when the input is empty.
const formatVoucherExpiration = input => {
  const value = String(input).trim()

  if(value.length === 0) return ''

  // case 1: eg. 365 lub "365"
  if (/^\d+$/.test(value)) {
    const days = Number(value)
    const suffix = days === 1 ? t('day') : t('days')

    return `${t('validFor')}: ${days} ${suffix.toLowerCase()}`
  }

  // case 2: eg. "2025-12-30"
  const dateObj = new Date(value)
  const normalized = extractDate(dateObj)

  return `${t('validUntilShort')}: ${normalized}`
}

// Returns the final expiration Date object based on baseDate and input.
// baseDate: a Date object used as the starting point.
// input: can be either a number/string representing a number of days
//        or a date string in a format accepted by the Date constructor.
// If input contains only digits, the function adds that number of days to baseDate.
// If input is a date string, it returns a new Date based on that value.
// Returns null for empty input.
const getVoucherExpirationDate = (baseDate, input) => {
  const value = String(input).trim()
  if (value.length === 0) return new Date()

  // eg. 365
  if (/^\d+$/.test(value)) {
    const days = Number(value)
    const date = new Date(baseDate)
    date.setDate(date.getDate() + days)

    return date
  }

  // eg. "2025-12-30"
  return new Date(value)
}

const handleMobileSearchFocus = (event) => {
  adjustMobileScroll(mobile, event?.target ?? event?.currentTarget, '.event-dialog-card-text', true)
}

const handleMobileSearchBlur = (event) => {
  adjustMobileScroll(mobile, event?.target ?? event?.currentTarget, '.event-dialog-card-text', false)
}

const recalculateVoucherPrice = (product) => {
  if (product.voucherDetails?.type !== 2) return

  if (Array.isArray(product.voucherDetails.services)) {
    let sum = 0
    product.voucherDetails.services.forEach(service => {
      const uses = Number(service.usesLeft) || 0
      let price = Number(service.price) || 0
      if (price === 0) price = 1
      sum += uses * price
    })
    //const discount = sum * Number(product.voucherDetails.discountMultiplier) || 0
    const discount = Math.round(
      (sum * Number(product.voucherDetails.discountMultiplier) + Number.EPSILON),
    ) || 0

    const finalPrice = sum - discount

    product.price = Math.max(0, Number(finalPrice.toFixed(2)))
    productsStore.updateProductPricing(product)
  }
}

//👉 Initialization
onMounted(() => {
  selectedSeller.value = eventStore.selectedSeller
})
</script>

<template>
  <VSheet
    class="product-selector-container"
    :class="mobile ? 'mb-6' : ''"
  >
    <VRow dense>
      <VCol
        v-if="!modalStore.modals.calendarSales && eventStore.allowSave"
        cols="12"
        md="4"
      >
        <VSelect
          v-model="eventStore.selectedProductType"
          :disabled="!eventStore.allowSave || eventStore.isSaving || modalStore.modals.calendarSales"
          color="secondary"
          density="compact"
          :label="$t('destination')"
          :items="[
            { value: 1, title: t('sale') },
            { value: 2, title: t('consumption') },
          ]"
          item-value="value"
          item-title="title"
          required
          :error-messages="productDestinationError"
        />
      </VCol>
      <VSpacer v-if="!modalStore.modals.calendarSales" />
      <VCol
        cols="12"
        :md="modalStore.modals.calendarSales ? 12 : 8"
      >
        <!-- Desktop: VAutocomplete -->
        <VAutocomplete
          v-if="eventStore.allowSave && !mobile"
          ref="productAutocompleteRef"
          v-model="selectedProduct"
          autocomplete="off"
          :label="$t('searchProduct')"
          :placeholder="$t('type_at_least_1char')"
          density="compact"
          color="secondary"
          :items="filteredProducts"
          :item-title="item => item ? item.title : ''"
          :item-value="item => item.id"
          :auto-select-first="!filteredProducts.find(p => p.notFound)"
          hide-no-data
          no-filter
          return-object
          :multiple="false"
          :loading="productsStore.isLoading"
          :disabled="!eventStore.allowSave"
          chips
          menu-icon=""
          @update:search="productsStore.fetchProducts"
          @update:model-value="addProduct"
          @focus="handleMobileSearchFocus"
          @blur="handleMobileSearchBlur"
        >
          <template #append-inner>
            <VIcon
              size="18"
              icon="ri-search-line"
              style="opacity: 0.5!important"
            />
          </template>

          <template #item="{item, props}">
            <VListItem
              v-bind="props"
              :disabled="item.raw.notFound"
            >
              <div v-if="!item.raw.notFound">
                <VListItemSubtitle>
                  <span>{{ formatNumber(item.raw.price) }} {{ calendarStore.currency }}</span>
                  <span
                    v-if="!item.raw.voucherDetails?.type"
                    class="ml-1"
                  >/ {{ item.raw.capacity }}</span>
                  <span
                    v-if="item.raw.voucherDetails?.timeout"
                    class="ml-1"
                  >/ {{ formatVoucherExpiration(item.raw.voucherDetails?.timeout) }}</span>
                </VListItemSubtitle>
                <VListItemSubtitle v-if="item.raw.ean || item.raw.actualStorageQuantityText">
                  <span v-if="item.raw.ean">EAN: {{ item.raw.ean }}</span>
                  <span v-if="item.raw.ean && item.raw.actualStorageQuantityText"> / </span>
                  <span v-if="item.raw.actualStorageQuantityText">{{ $t('warehouse_status') }}: {{ item.raw.actualStorageQuantityText }}</span>
                </VListItemSubtitle>
              </div>
            </VListItem>
          </template>

          <template #append>
            <VSelect
              v-model="selectedSeller"
              class="avatar-employee-selector pa-0"
              variant="plain"
              :item-title="item => `${item.title}`"
              :item-value="item => `${item.id}`"
              :items="calendarStore.employeesCalendars"
              max-width="30"
              return-object
            >
              <template #selection="{ item }">
                <div class="d-flex align-center items-center">
                  <EmployeeAvatar
                    :employee="item.raw"
                    class="cursor-pointer"
                  />
                </div>
              </template>
              <template #item="{ item, props }">
                <VListItem
                  density="compact"
                  v-bind="props"
                  :value="item"
                  :ripple="false"
                >
                  <template #prepend>
                    <EmployeeAvatar
                      :employee="item.raw"
                      size="small"
                      :show-tooltip="false"
                    />
                  </template>
                  <template #title>
                    {{ item.raw.title }}
                  </template>
                </VListItem>
              </template>
            </VSelect>
          </template>
        </VAutocomplete>

        <!-- Mobile: MobileAutocomplete -->
        <MobileAutocomplete
          v-if="eventStore.allowSave && mobile"
          v-model="selectedProduct"
          :label="$t('searchProduct')"
          :placeholder="$t('type_at_least_3chars')"
          :inner-label="$t(eventStore.selectedProductType === 1 ? 'search_product_for_sale' : 'search_product_for_usage') "
          :inner-placeholder="$t('type_at_least_3chars')"
          :items="filteredProducts"
          :item-title="item => item ? item.title : ''"
          :item-value="item => item.id"
          hide-no-data
          no-filter
          return-object
          :loading="productsStore.isLoading"
          :disabled="!eventStore.allowSave"
          @update:search="productsStore.fetchProducts"
          @update:model-value="addProduct"
          @blur="handleMobileSearchBlur"
        >
          <template #item="{ item, props }">
            <VListItem
              v-bind="props"
              :disabled="item.raw.notFound"
            >
              <div v-if="!item.raw.notFound">
                <VListItemSubtitle>
                  <span>{{ formatNumber(item.raw.price) }} {{ calendarStore.currency }}</span>
                  <span
                    v-if="!item.raw.voucherDetails?.type"
                    class="ml-1"
                  >/ {{ item.raw.capacity }}</span>
                  <span
                    v-if="item.raw.voucherDetails?.timeout"
                    class="ml-1"
                  >/ {{ formatVoucherExpiration(item.raw.voucherDetails?.timeout) }}</span>
                </VListItemSubtitle>
                <VListItemSubtitle v-if="item.raw.ean || item.raw.actualStorageQuantityText">
                  <span v-if="item.raw.ean">EAN: {{ item.raw.ean }}</span>
                  <span v-if="item.raw.ean && item.raw.actualStorageQuantityText"> / </span>
                  <span v-if="item.raw.actualStorageQuantityText">{{ $t('warehouse_status') }}: {{ item.raw.actualStorageQuantityText }}</span>
                </VListItemSubtitle>
              </div>
            </VListItem>
          </template>
        </MobileAutocomplete>
      </VCol>
    </VRow>
    <!--SALE PRODUCTS-->
    <VSheet
      v-if="saleProducts.length > 0"
      id="selected-products-container"
      class="selected-products-container border-thin rounded-lg my-4"
    >
      <VCardTitle
        class="darken-bg rounded-t-lg"
        :class="props.inCollapse ? 'pa-2' : ''"
      >
        <VIcon icon="ri-shopping-cart-line" />
        {{ $t('sale') }}:
      </VCardTitle>
      <VListItem
        v-for="(product, idx) in saleProducts"
        :key="`sale-${idx}-${product.id}`"
        class="rounded-b-lg"
        :class="[
          idx % 2 === 1 ? 'darken-bg' : '',
          mobile ? 'd-flex flex-column ga-2' : ''
        ]"
      >
        <div class="d-flex justify-space-between flex-wrap align-center">
          <div class="w-100">
            <!-- Product content here -->
            <VListItemTitle class="d-flex align-center pt-2 ga-2">
              <span
                v-if="!product.isVoucher"
                class="break-spaces font-weight-bold text-h6"
              >
                {{ product.title }}  {{ product.capacity ? `(${product.capacity})` : '' }}
              </span>
              <div
                v-else
                class=""
              >
                <span class="break-spaces font-weight-bold text-h6">
                  {{ product.voucherDetails?.type === 2 ? $t('pass') : $t('voucher') }}
                </span>
                <small
                  v-if="product.voucherDetails?.discount > 0"
                  class="ml-1 text-error"
                >({{ $t('discount') }} {{ formatNumber(product.voucherDetails?.discount) }}%)</small>
              </div>
              <div v-if="showProductStoreLink && !product.isVoucher">
                <VBtn
                  size="small"
                  color="gray"
                  variant="text"
                  icon="ri-pencil-line"
                  @click.stop="openStorageProductDialog(product)"
                />
                <VTooltip
                  v-if="!mobile"
                  activator="parent"
                >
                  {{ $t('editProduct') }}
                </VTooltip>
              </div>
            </VListItemTitle>

            <div
              v-if="product.isVoucher"
              class="py-2 mt-1"
            >
              <div class="d-flex align-center flex-wrap ga-2 mb-3">
                <VTextField
                  v-model="product.title"
                  style="min-width: 160px"
                  :disabled="!eventStore.allowSave || !product.voucherDetails?.isValueEditable || eventStore.wasFinalisedAlready"
                  :label="$t('theName')"
                  color="secondary"
                  class="xs-input"
                  hide-details
                  density="compact"
                />
                <VTextField
                  v-if="product.isVoucher"
                  v-model="product.voucherDetails.identifier"
                  style="min-width: 160px"
                  disabled
                  :label="product.voucherDetails?.type === 2 ? $t('ticketID') : $t('voucherID')"
                  color="secondary"
                  class="xs-input w-100"
                  :max-width="xs ? '100%' : '160px'"
                  hide-details
                  density="compact"
                />
              </div>
              <div v-if="product.voucherDetails?.type === 2">
                <ul class="mt-n2 mb-3 ml-1">
                  <li
                    v-for="(service, idx) in product.voucherDetails?.services"
                    :key="product.id+`-`+idx"
                    class="d-flex align-center items-center mt-1"
                  >
                    <VIcon
                      icon="ri-corner-down-right-line"
                      class="mr-1"
                      :color="service.isDeleted ? 'error' : ''"
                    />
                    <div class="mr-2 w-100">
                      <VAutocomplete
                        v-if="!mobile"
                        :model-value="service"
                        return-object
                        class="compact-autocomplete"
                        :class="{ 'service-deleted-autocomplete': service.isDeleted }"
                        density="compact"
                        color="secondary"
                        :label="$t(service.isDeleted ? 'service_is_deleted' : 'service')"
                        :custom-filter="filterByName"
                        :items="uniqueServicesForFiltering"
                        :disabled="!eventStore.allowSave || !product.voucherDetails?.isValueEditable || eventStore.wasFinalisedAlready"
                        :item-title="item => `${item.name} ${item.duration ? '('+item.duration+' min)' : ''}`"
                        :item-value="item => item.id"
                        @update:model-value="(selectedService) => {
                          if(selectedService) {
                            service.id = selectedService.id
                            service.name = selectedService.name
                            service.price = selectedService.price
                            service.duration = selectedService.duration
                            service.isDeleted = selectedService.isDeleted
                          }
                          recalculateVoucherPrice(product)
                        }"
                      />
                      <MobileAutocomplete
                        v-else
                        :model-value="service"
                        class="compact-autocomplete"
                        :class="{ 'service-deleted-autocomplete': service.isDeleted }"
                        :disabled="!eventStore.allowSave || !product.voucherDetails?.isValueEditable || eventStore.wasFinalisedAlready"
                        :items="uniqueServicesForFiltering"
                        :item-title="item => `${item.name} ${item.duration ? '('+item.duration+' min)' : ''}`"
                        :item-value="item => item.id"
                        :label="$t(service.isDeleted ? 'service_is_deleted' : 'service')"
                        :placeholder="null"
                        :inner-label="$t(service.isDeleted ? 'service_is_deleted' : 'service')"
                        :inner-placeholder="null"
                        :clearable="false"
                        :custom-filter="filterByName"
                        return-object
                        :no-data-text="$t('no_results')"
                        @update:model-value="(selectedService) => {
                          if(selectedService) {
                            service.id = selectedService.id
                            service.name = selectedService.name
                            service.price = selectedService.price
                            service.duration = selectedService.duration
                            service.isDeleted = selectedService.isDeleted
                          }
                          recalculateVoucherPrice(product)
                        }"
                      />
                    </div>
                    <div style="min-width: 90px; margin-top: 5px;">
                      <VNumberInput
                        v-model="service.usesLeft"
                        :label="$t('uses')"
                        variant="outlined"
                        color="secondary"
                        class="xs-input xs-input-padding"
                        density="compact"
                        max-width="160px"
                        :disabled="!eventStore.allowSave || !product.voucherDetails?.isValueEditable || eventStore.wasFinalisedAlready"
                        hide-details
                        :min="0"
                        :max="999"
                        hide-spin-buttons
                        maxlength="3"
                        control-variant="hidden"
                        @update:model-value="recalculateVoucherPrice(product)"
                      />
                    </div>
                    <div
                      v-if="eventStore.allowSave"
                      style="margin-top: 5px;"
                      class="ml-1"
                    >
                      <VBtn
                        color="gray"
                        variant="text"
                        size="x-small"
                        icon="ri-delete-bin-line"
                        :disabled="!eventStore.allowSave || !product.voucherDetails?.isValueEditable || eventStore.wasFinalisedAlready"
                        @click.stop="() => { product.voucherDetails.services.splice(idx, 1); recalculateVoucherPrice(product); }"
                      />
                      <VTooltip
                        v-if="!mobile"
                        activator="parent"
                        location="top"
                      >
                        {{ $t('delete') }}
                      </VTooltip>
                    </div>
                  </li>
                  <li
                    v-if="eventStore.allowSave && !eventStore.wasFinalisedAlready"
                    class="d-flex align-center items-center mt-1 py-1"
                  >
                    <VIcon
                      icon="ri-corner-down-right-line"
                      class="mr-1"
                    />
                    <VBtn
                      density="compact"
                      variant="flat"
                      color="secondary"
                      size="small"
                      class="px-2 text-none"
                      :disabled="!eventStore.allowSave || !product.voucherDetails?.isValueEditable || eventStore.wasFinalisedAlready"
                      @click="!product.voucherDetails.services ? product.voucherDetails.services = [{ id: null, name: '', usesLeft: 1 }] : product.voucherDetails.services.push({ id: null, name: '', usesLeft: 1 })"
                    >
                      Dodaj kolejną usługę
                    </VBtn>
                  </li>
                </ul>
              </div>
              <div class="d-flex justify-space-between align-center ga-2">
                <div class="d-flex align-center flex-wrap ga-2">
                  <div
                    v-if="product.isVoucher"
                    style="max-width: 140px;"
                  >
                    <DatePicker
                      :model-value="product.voucherExpirationDate"
                      :inline="false"
                      :label-text="$t('validUntil')"
                      :min-date="eventStore.selectedDate"
                      :disabled="!eventStore.allowSave || !product.voucherDetails?.isValueEditable || eventStore.wasFinalisedAlready"
                      teleport
                      class="xs-input"
                      @update:model-value="val => { product.voucherDetails.timeout = extractDate(val, '-'); product.voucherExpirationDate = val; }"
                    />
                  </div>
                  <div v-if="product.voucherDetails?.type === 1">
                    <VNumberInput
                      v-model="product.voucherDetails.voucherValue"
                      :disabled="!eventStore.allowSave || !product.voucherDetails.isValueEditable"
                      :label="$t('voucherValue')"
                      color="secondary"
                      class="xs-input"
                      min-width="130px"
                      :suffix="calendarStore.currency"
                      hide-details
                      density="compact"
                      variant="outlined"
                      :min="0"
                      :max="99999"
                      hide-spin-buttons
                      :precision="2"
                      :min-fraction-digits="0"
                      maxlength="7"
                      control-variant="hidden"
                      @update:model-value="() => {
                        if (product.voucherDetails.voucherValue === null || product.voucherDetails.voucherValue === '') product.voucherDetails.voucherValue = 0
                        productsStore.updateProductPricing(product)
                      }"
                    />
                  </div>
                  <VNumberInput
                    v-model="product.price"
                    :disabled="!eventStore.allowSave"
                    :label="$t('single_item_price')"
                    color="secondary"
                    class="xs-input"
                    min-width="110px"
                    max-width="110px"
                    :suffix="calendarStore.currency"
                    hide-details
                    density="compact"
                    variant="outlined"
                    :min="0"
                    :max="99999"
                    hide-spin-buttons
                    :precision="2"
                    :min-fraction-digits="0"
                    maxlength="7"
                    control-variant="hidden"
                    @update:model-value="() => {
                      if (product.price === null || product.price === '') product.price = 0
                      productsStore.updateProductPricing(product)
                    }"
                  />
                </div>
                <div
                  v-if="!xs"
                  class="d-flex align-center justify-end flex-wrap ga-2"
                  style="min-width: 70px"
                >
                  <div>
                    <VBtn
                      v-show="eventStore.allowSave"
                      color="info"
                      variant="text"
                      size="30"
                      icon="ri-percent-line"
                      @click="openEditProductDialog(product, idx)"
                    />
                    <VTooltip activator="parent">
                      {{ $t('applyDiscount') }}
                    </VTooltip>
                  </div>
                  <div class="ml-n1">
                    <VBtn
                      v-show="eventStore.allowSave"
                      size="small"
                      color="gray"
                      variant="text"
                      icon="ri-delete-bin-line"
                      @click.stop="removeProduct(product)"
                    />
                    <VTooltip
                      v-if="!mobile"
                      activator="parent"
                    >
                      {{ $t('delete') }}
                    </VTooltip>
                  </div>

                  <div
                    id="personSellingProduct"
                    :class="!eventStore.allowSave ? `pointer-events-none opacity-80` : ``"
                    style="cursor: pointer"
                    @click.stop="openSellerDialog(product)"
                  >
                    <EmployeeAvatar :employee="product.seller" />
                  </div>
                </div>
              </div>
              <div class="d-inline-flex ga-1 align-center items-center xs-font-size mt-n1">
                <span class="xs-font-size text-high-emphasis">
                  {{ $t('value') }}:
                </span>
                <span
                  class="font-weight-medium text-high-emphasis"
                  :class="{ 'text-decoration-line-through mr-1': product.discount > 0 }"
                >
                  {{ formatNumber(calculateValue(product)) }}&nbsp;{{ calendarStore.currency }}
                </span>

                <span
                  v-if="product.discountType && product.discountValue > 0"
                  class="font-weight-bold text-green"
                >
                  {{ formatNumber(product.finalPrice) }}&nbsp;{{ calendarStore.currency }}
                </span>
                <span
                  v-if="product.voucherDetails.voucherValue !== product.price && !xs && product.voucherDetails.type === 1"
                  class="text-error ml-1 lh-1 my-1"
                >
                  {{ $t('Warning_Voucher_price_diff_than_its_value') }}
                </span>
              </div>
              <div
                v-if="product.voucherDetails.voucherValue !== product.price && xs && product.voucherDetails.type === 1"
                class="text-error lh-1 xs-font-size"
              >
                {{ $t('Warning_Voucher_price_diff_than_its_value') }}
              </div>
            </div>
            <div
              v-if="Number(product.type) === 1 && !product.isVoucher"
              class="py-2 mt-1 w-100"
            >
              <div class="d-flex justify-space-between align-center ga-2">
                <div class="d-flex align-center flex-wrap ga-2">
                  <VNumberInput
                    v-model="product.price"
                    :disabled="!eventStore.allowSave"
                    :label="$t('single_item_price')"
                    color="secondary"
                    class="xs-input"
                    min-width="110px"
                    max-width="210px"
                    :suffix="calendarStore.currency"
                    hide-details
                    density="compact"
                    variant="outlined"
                    :min="0"
                    :max="99999"
                    hide-spin-buttons
                    :precision="2"
                    :min-fraction-digits="0"
                    maxlength="7"
                    control-variant="hidden"
                    @update:model-value="() => {
                      if (product.price === null || product.price === '') product.price = 0
                      productsStore.updateProductPricing(product)
                    }"
                  />
                  <VNumberInput
                    v-model="product.quantity"
                    :label="$t('quantity')"
                    color="secondary"
                    class="xs-input xs-input-padding"
                    density="compact"
                    variant="outlined"
                    min-width="110px"
                    hide-details
                    :min="0"
                    :max="999"
                    hide-spin-buttons
                    maxlength="3"
                    control-variant="hidden"
                    :disabled="!eventStore.allowSave"
                    @update:model-value="() => {
                      if (product.quantity === null || product.quantity === '') product.quantity = 1
                      productsStore.updateProductPricing(product)
                    }"
                  />
                </div>
                <div
                  v-if="!xs"
                  class="d-flex align-center flex-wrap ga-2"
                >
                  <div>
                    <VBtn
                      v-show="eventStore.allowSave"
                      color="info"
                      variant="text"
                      size="30"
                      icon="ri-percent-line"
                      @click="openEditProductDialog(product, idx)"
                    />
                    <VTooltip activator="parent">
                      {{ $t('applyDiscount') }}
                    </VTooltip>
                  </div>
                  <div class="ml-n1">
                    <VBtn
                      v-show="eventStore.allowSave"
                      size="small"
                      color="gray"
                      variant="text"
                      icon="ri-delete-bin-line"
                      @click.stop="removeProduct(product)"
                    />
                    <VTooltip
                      v-if="!mobile"
                      activator="parent"
                    >
                      {{ $t('delete') }}
                    </VTooltip>
                  </div>

                  <div
                    :class="!eventStore.allowSave ? `pointer-events-none opacity-80` : ``"
                    style="cursor: pointer"
                    @click.stop="openSellerDialog(product)"
                  >
                    <!-- Display avatars for selected employees -->
                    <!--product.seller?.color-->
                    <EmployeeAvatar :employee="product.seller" />
                  </div>
                </div>
              </div>
              <div class="d-inline-flex ga-1 align-center items-center xs-font-size mt-n1">
                <span class="xs-font-size text-high-emphasis">
                  {{ $t('value') }}:
                </span>
                <span
                  class="font-weight-medium text-high-emphasis"
                  :class="{ 'text-decoration-line-through mr-1': product.discount > 0 }"
                >
                  {{ formatNumber(calculateValue(product)) }}&nbsp;{{ calendarStore.currency }}
                </span>

                <span
                  v-if="product.discountType && product.discountValue > 0"
                  data-testid="saleSection-after-discount-value"
                  class="font-weight-bold text-green"
                >
                  {{ formatNumber(product.finalPrice) }}&nbsp;{{ calendarStore.currency }}
                </span>
              </div>
            </div>
            <VListItemSubtitle
              v-if="Number(product.type) === 2 && !product.isVoucher"
              class="py-2 mt-1"
            >
              <VNumberInput
                v-model="product.quantity"
                class="xs-input"
                :label="$t('quantity')"
                max-width="90px"
                color="secondary"
                density="compact"
                :suffix="product.unit"
                variant="outlined"
                hide-details
                :min="0"
                :max="999"
                hide-spin-buttons
                maxlength="3"
                control-variant="hidden"
                :disabled="!eventStore.allowSave"
              />
            </VListItemSubtitle>
          </div>
          <div
            v-if="xs"
            class="product-actions ml-auto"
          >
            <div class="d-flex ga-3">
              <div>
                <VBtn
                  v-show="eventStore.allowSave"
                  color="info"
                  variant="text"
                  size="32"
                  icon="ri-percent-line"
                  @click="openEditProductDialog(product, idx)"
                />
                <VTooltip
                  v-if="!mobile"
                  activator="parent"
                >
                  {{ $t('applyDiscount') }}
                </VTooltip>
              </div>

              <div class="ml-n1">
                <VBtn
                  v-show="eventStore.allowSave"
                  size="small"
                  color="gray"
                  variant="text"
                  icon="ri-delete-bin-line"
                  @click.stop="removeProduct(product)"
                />
                <VTooltip
                  v-if="!mobile"
                  activator="parent"
                >
                  {{ $t('delete') }}
                </VTooltip>
              </div>

              <div
                :class="!eventStore.allowSave ? `pointer-events-none opacity-80` : ``"
                style="cursor: pointer"
                @click.stop="openSellerDialog(product)"
              >
                <!-- Display avatars for selected employees -->
                <!--product.seller?.color-->
                <EmployeeAvatar :employee="product.seller" />
              </div>
            </div>
          </div>
        </div>
      </VListItem>
    </VSheet>

    <!--CONSUMPTION PRODUCTS-->
    <VSheet
      v-if="consumptionProducts.length > 0"
      class="selected-products-container border-thin rounded-lg my-4"
    >
      <VCardTitle class="darken-bg rounded-t-lg">
        <VIcon icon="ri-flask-line" />
        {{ $t('consumption') }}:
      </VCardTitle>
      <VListItem
        v-for="(product, idx) in consumptionProducts"
        :key="`consumption-${idx}-${product.id}`"
        :disabled="!eventStore.allowSave"
        class="rounded-b-lg"
        :class="{ 'darken-bg': idx % 2 === 1 }"
      >
        <div class="d-flex justify-space-between flex-wrap align-center">
          <div>
            <!-- Product consumption content here -->
            <VListItemTitle class="d-flex align-center pt-2 ga-1 ">
              <VTooltip activator="parent">
                {{ $t('consumption') }}
              </VTooltip>

              <span
                v-if="!product.isVoucher"
                class="break-spaces font-weight-bold text-h6"
              >
                {{ product.title }}  {{ product.capacity ? `(${product.capacity})` : '' }}
              </span>
              <VTextField
                v-else
                v-model="product.title"
                :disabled="!eventStore.allowSave || !product.voucherDetails?.isValueEditable || eventStore.wasFinalisedAlready"
                :label="$t('theName')"
                color="secondary"
                class="xs-input"
                hide-details
                density="compact"
              />
              <VTextField
                v-if="product.isVoucher"
                v-model="product.voucherDetails.identifier"
                disabled
                :label="product.voucherDetails?.type === 2 ? $t('ticketID') : $t('voucherID')"
                color="secondary"
                class="xs-input"
                max-width="160px"
                hide-details
                density="compact"
              />
              <div v-if="showProductStoreLink && !product.isVoucher">
                <VBtn
                  size="small"
                  color="gray"
                  variant="text"
                  icon="ri-pencil-line"
                  @click.stop="openStorageProductDialog(product)"
                />
                <VTooltip
                  v-if="!mobile"
                  activator="parent"
                >
                  {{ $t('editProduct') }}
                </VTooltip>
              </div>
            </VListItemTitle>

            <VListItemSubtitle
              v-if="product.isVoucher"
              class="py-2 mt-1"
            >
              <div v-if="product.voucherDetails?.type === 2 && product.voucherDetails?.services && product.voucherDetails?.services.length > 0">
                <ul class="mt-n2 mb-3 ml-1">
                  <li
                    v-for="(service, idx) in product.voucherDetails?.services"
                    :key="product.id+`-`+idx"
                    class="d-flex align-center items-center"
                  >
                    <VIcon
                      icon="ri-corner-down-right-line"
                      class="mr-1"
                    />
                    <!--                <span style="line-height: 0.8rem;">{{ service.serviceName }}</span>-->
                    <!--                @update:model-value="updateServiceId(idx, $event)"-->
                    <div class="mr-2">
                      <VAutocomplete
                        :model-value="service"
                        return-object
                        class="compact-autocomplete"
                        density="compact"
                        color="secondary"
                        :label="$t('service')"
                        :custom-filter="filterByName"
                        :items="uniqueServicesForFiltering"
                        :disabled="!eventStore.allowSave || !product.voucherDetails?.isValueEditable || eventStore.wasFinalisedAlready"
                        :item-title="item => item ? item.name : '??'"
                        :item-value="item => item.id"
                        @update:model-value="(selectedService) => {
                          if(selectedService) {
                            service.id = selectedService.id
                            service.name = selectedService.name
                          }
                        }"
                      />
                    </div>
                    <!--                <span class="ml-1" style="min-width: 110px;"><VIcon icon="ri-close-line"/> {{ service.usesLeft }} użyć</span>-->
                    <div style="min-width: 90px; margin-top: 5px;">
                      <VNumberInput
                        v-model="service.usesLeft"
                        :label="$t('uses')"
                        variant="outlined"
                        color="secondary"
                        class="xs-input xs-input-padding"
                        density="compact"
                        max-width="160px"
                        :disabled="!eventStore.allowSave || !product.voucherDetails?.isValueEditable || eventStore.wasFinalisedAlready"
                        hide-details
                        :min="0"
                        :max="999"
                        hide-spin-buttons
                        maxlength="3"
                        control-variant="hidden"
                      />
                    </div>
                  </li>
                </ul>
              </div>
              <div class="d-flex align-center flex-wrap ga-2">
                <div
                  v-if="product.voucherDetails?.type === 1"
                  style="min-width: 120px"
                >
                  <VNumberInput
                    v-model="product.voucherDetails.voucherValue"
                    :disabled="!eventStore.allowSave"
                    :label="$t('voucherValue')"
                    color="secondary"
                    class="xs-input"
                    min-width="130px"
                    :suffix="calendarStore.currency"
                    hide-details
                    density="compact"
                    variant="outlined"
                    :min="0"
                    :max="99999"
                    hide-spin-buttons
                    :precision="2"
                    :min-fraction-digits="0"
                    maxlength="7"
                    control-variant="hidden"
                    @update:model-value="() => {
                      if (product.voucherDetails.voucherValue === null || product.voucherDetails.voucherValue === '') product.voucherDetails.voucherValue = 0
                      productsStore.updateProductPricing(product)
                    }"
                  />
                </div>
                <div style="min-width: 110px">
                  <VNumberInput
                    v-model="product.price"
                    :disabled="!eventStore.allowSave"
                    :label="$t('single_item_price')"
                    color="secondary"
                    class="xs-input"
                    max-width="110px"
                    :suffix="calendarStore.currency"
                    hide-details
                    density="compact"
                    variant="outlined"
                    :min="0"
                    :max="99999"
                    hide-spin-buttons
                    :precision="2"
                    :min-fraction-digits="0"
                    maxlength="7"
                    control-variant="hidden"
                    @update:model-value="() => {
                      if (product.price === null || product.price === '') product.price = 0
                      productsStore.updateProductPricing(product)
                    }"
                  />
                </div>
                <div
                  v-if="product.voucherDetails?.type === 1"
                  style="min-width: 80px"
                >
                  <VNumberInput
                    v-model="product.quantity"
                    :label="$t('quantity')"
                    color="secondary"
                    class="xs-input xs-input-padding"
                    density="compact"
                    variant="outlined"
                    hide-details
                    :min="0"
                    :max="999"
                    hide-spin-buttons
                    maxlength="3"
                    control-variant="hidden"
                    :disabled="!eventStore.allowSave"
                    @update:model-value="() => {
                      if (product.quantity === null || product.quantity === '') product.quantity = 1
                      productsStore.updateProductPricing(product)
                    }"
                  />
                </div>
                <div class="ml-n3">
                  <VBtn
                    v-show="eventStore.allowSave"
                    color="info"
                    variant="plain"
                    icon="ri-discount-percent-line"
                    @click="openEditProductDialog(product, idx)"
                  />
                  <VTooltip activator="parent">
                    {{ $t('applyDiscount') }}
                  </VTooltip>
                </div>
              </div>
              <div class="d-inline-flex ga-1 align-center items-center xs-font-size mt-n1">
                <span class="xs-font-size text-high-emphasis">
                  {{ $t('value') }}:
                </span>
                <span
                  class="font-weight-medium text-high-emphasis"
                  :class="{ 'text-decoration-line-through mr-1': product.discount > 0 }"
                >
                  {{ formatNumber(calculateValue(product)) }}&nbsp;{{ calendarStore.currency }}
                </span>

                <span
                  v-if="product.discountType && product.discountValue > 0"
                  class="font-weight-bold text-green"
                >
                  {{ formatNumber(product.finalPrice) }}&nbsp;{{ calendarStore.currency }}
                </span>
              </div>
            </VListItemSubtitle>
            <VListItemSubtitle
              v-if="Number(product.type) === 1 && !product.isVoucher"
              class="py-2 mt-1"
            >
              <div class="d-flex align-center flex-wrap ga-2">
                <div>
                  <VNumberInput
                    v-model="product.price"
                    :disabled="!eventStore.allowSave"
                    :label="$t('single_item_price')"
                    color="secondary"
                    class="xs-input"
                    min-width="110px"
                    max-width="210px"
                    :suffix="calendarStore.currency"
                    hide-details
                    density="compact"
                    variant="outlined"
                    :min="0"
                    :max="99999"
                    hide-spin-buttons
                    :precision="2"
                    :min-fraction-digits="0"
                    maxlength="7"
                    control-variant="hidden"
                    @update:model-value="() => {
                      if (product.price === null || product.price === '') product.price = 0
                      productsStore.updateProductPricing(product)
                    }"
                  />
                </div>
                <div style="min-width: 80px">
                  <VNumberInput
                    v-model="product.quantity"
                    :label="$t('quantity')"
                    color="secondary"
                    class="xs-input xs-input-padding"
                    density="compact"
                    variant="outlined"
                    hide-details
                    :min="0"
                    :max="999"
                    hide-spin-buttons
                    maxlength="3"
                    control-variant="hidden"
                    :disabled="!eventStore.allowSave"
                    @update:model-value="() => {
                      if (product.quantity === null || product.quantity === '') product.quantity = 1
                      productsStore.updateProductPricing(product)
                    }"
                  />
                </div>
                <div class="ml-n3">
                  <VBtn
                    v-show="eventStore.allowSave"
                    color="info"
                    variant="plain"
                    icon="ri-discount-percent-line"
                    @click="openEditProductDialog(product, idx)"
                  />
                  <VTooltip activator="parent">
                    {{ $t('applyDiscount') }}
                  </VTooltip>
                </div>
              </div>
              <div class="d-inline-flex ga-1 align-center items-center xs-font-size mt-n1">
                <span class="xs-font-size text-high-emphasis">
                  {{ $t('value') }}:
                </span>
                <span
                  class="font-weight-medium text-high-emphasis"
                  :class="{ 'text-decoration-line-through mr-1': product.discount > 0 }"
                >
                  {{ formatNumber(calculateValue(product)) }}&nbsp;{{ calendarStore.currency }}
                </span>

                <span
                  v-if="product.discountType && product.discountValue > 0"
                  class="font-weight-bold text-green"
                >
                  {{ formatNumber(product.finalPrice) }}&nbsp;{{ calendarStore.currency }}
                </span>
              </div>
            </VListItemSubtitle>
            <div
              v-if="Number(product.type) === 2 && !product.isVoucher"
              class="py-2 mt-1"
            >
              <VNumberInput
                v-model="product.quantity"
                class="xs-input"
                :label="$t('quantity')"
                min-width="140px"
                max-width="150px"
                color="secondary"
                density="compact"
                :suffix="product.unit"
                variant="outlined"
                hide-details
                :min="0"
                :max="99999"
                maxlength="7"
                :precision="2"
                :min-fraction-digits="0"
                hide-spin-buttons
                control-variant="hidden"
                :disabled="!eventStore.allowSave"
              />
            </div>
          </div>
          <div class="product-actions ml-auto">
            <div class="d-flex ga-2">
              <div>
                <VBtn
                  v-show="eventStore.allowSave"
                  size="small"
                  color="gray"
                  variant="text"
                  icon="ri-delete-bin-line"
                  @click.stop="removeProduct(product)"
                />
                <VTooltip activator="parent">
                  {{ $t('delete') }}
                </VTooltip>
              </div>
              <div @click.stop="openSellerDialog(product)">
                <!-- Display avatars for selected employees -->
                <!--product.seller?.color-->
                <EmployeeAvatar
                  :employee="product.seller"
                  :style="eventStore.allowSave ? `cursor: pointer` : `cursor: default!important`"
                />
              </div>
            </div>
          </div>
        </div>
      </VListItem>
    </VSheet>
  </VSheet>
  <EditProductDialog ref="editProductDialog" />
  <EditStorageProductDialog v-if="modalStore.modals.editStorageProduct" />
  <!-- Add Supplier Dialog - always must be included with EditStorageProductDialog -->
  <AddSupplierDialog v-if="modalStore.modals.addSupplier" />

  <!-- Manage product seller  -->
  <VDialog
    v-model="modalStore.modals.selectSeller"
    max-width="350px"
  >
    <VCard>
      <template #title>
        <div class="d-flex justify-space-between align-center">
          {{ $t('employees') }}
          <VIcon
            class="ml-auto"
            icon="ri-close-line"
            @click="modalStore.closeModal('selectSeller')"
          />
        </div>
      </template>

      <VCardText>
        <div class="mb-2">
          <VRadioGroup
            v-model="tempSelectedEmployee"
            density="compact"
          >
            <VRadio
              v-for="(item, index) in calendarStore.employeesCalendars"
              :key="item.id ?? index"
              :value="item"
              class="mb-2"
            >
              <template #label>
                <div class="d-flex align-center ga-2">
                  <EmployeeAvatar :employee="item" />
                  <span>{{ item.title }}</span>
                </div>
              </template>
            </VRadio>
          </VRadioGroup>
        </div>
        <!-- Only selected employee performs service case  -->
      </VCardText>
      <template #actions>
        <div class="d-flex pa-2 flex-fill justify-space-between">
          <VBtn
            color="secondary"
            variant="outlined"
            @click="modalStore.closeModal('selectSeller')"
          >
            {{ $t('cancel') }}
          </VBtn>
          <VBtn
            variant="flat"
            color="green"
            @click="saveSelectedSeller"
          >
            {{ $t('save') }}
          </VBtn>
        </div>
      </template>
    </VCard>
  </VDialog>
</template>

<style scoped lang="scss">
:deep(.service-deleted-autocomplete .v-label),
:deep(.service-deleted-autocomplete .v-autocomplete__selection-text),
:deep(.service-deleted-autocomplete .v-field__input) {
  color: rgb(var(--v-theme-error)) !important;
}

:deep(.service-deleted-autocomplete .v-field__outline) {
  color: rgb(var(--v-theme-error)) !important;
}
</style>
